/**
 * Menghitung selisih hari (malam) antara dua tanggal, dengan mempertimbangkan 
 * batas tanggal kalender yang terlihat (viewport).
 * 
 * @param start - Tanggal mulai event.
 * @param end - Tanggal berakhir event (tanggal checkout, bukan malam terakhir).
 * @param startDay - Tanggal mulai dari tampilan kalender (viewport start).
 * @param endDay - Tanggal berakhir dari tampilan kalender (viewport end).
 * @returns Jumlah hari/malam event yang terlihat di dalam viewport.
 */
export const getDayDiff = (
    start: string | Date | undefined,
    end: string | Date | undefined,
    startDay?: string | Date,
    endDay?: string | Date
): number => {
    // 1. Validasi input dasar
    if (!start || !end) {
        return 0;
    }

    // 2. Konversi semua input menjadi objek Date dan normalkan ke awal hari (UTC)
    // Ini penting untuk menghindari masalah zona waktu dan jam.
    // 'startOfDay' dari date-fns adalah cara yang bagus, atau kita bisa set manual.
    const eventStartDate = new Date(start);
    eventStartDate.setUTCHours(0, 0, 0, 0);

    const eventEndDate = new Date(end);
    eventEndDate.setUTCHours(0, 0, 0, 0);

    // 3. Jika tidak ada batas viewport, hitung durasi total event
    if (!startDay || !endDay) {
        const timeDiff = eventEndDate.getTime() - eventStartDate.getTime();
        return timeDiff / (1000 * 60 * 60 * 24);
    }

    // 4. Terapkan logika "clamping" (penjepitan) dengan batas viewport
    const viewStartDate = new Date(startDay);
    viewStartDate.setUTCHours(0, 0, 0, 0);

    const viewEndDate = new Date(endDay);
    viewEndDate.setUTCHours(0, 0, 0, 0);

    // Tentukan tanggal mulai efektif: ambil yang paling akhir antara start event dan start viewport.
    const clampedStartDate = new Date(Math.max(eventStartDate.getTime(), viewStartDate.getTime()));

    // Tentukan tanggal berakhir efektif: ambil yang paling awal antara end event dan end viewport.
    const clampedEndDate = new Date(Math.min(eventEndDate.getTime(), viewEndDate.getTime()));

    // 5. Hitung selisih dari tanggal yang sudah dijepit
    // Jika event sepenuhnya di luar viewport, selisihnya akan <= 0.
    const timeDiff = clampedEndDate.getTime() - clampedStartDate.getTime();

    if (timeDiff <= 0) {
        return 0;
    }

    // Konversi selisih waktu dari milidetik ke hari
    const dayDiff = timeDiff / (1000 * 60 * 60 * 24);

    return dayDiff;
};

/**
 * Menghitung jumlah malam antara tanggal check-in dan check-out.
 * @param checkIn - Tanggal check-in dalam format ISO string (yyyy-mm-dd atau ISO full date)
 * @param checkOut - Tanggal check-out dalam format ISO string
 * @returns Jumlah malam (number)
 */
export function calculateNights(checkIn: string | Date, checkOut: string | Date): number {
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);

    // Pastikan tanggal valid
    if (isNaN(checkInDate.getTime()) || isNaN(checkOutDate.getTime())) {
        throw new Error("Invalid date format");
    }

    // Hitung selisih waktu (ms), lalu konversi ke hari
    const diffTime = checkOutDate.getTime() - checkInDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : 0;
}

/**
 * Mengembalikan teks jumlah malam dalam format "1 night" atau "2 nights"
 * @param nights - Jumlah malam
 * @returns String seperti "1 night" atau "3 nights"
 */
export function calculateNightsText(nights: number): string {
    const label = nights === 1 ? "Night" : "Nights";
    return `${nights} ${label}`;
}

/**
 * Cek apakah dua rentang tanggal [startA, endA) dan [startB, endB) tumpang tindih.
 * Konvensi checkout EXCLUSIVE (standar hotel) — booking yang checkout persis di
 * tanggal X TIDAK dianggap bentrok dengan blok lain yang mulai di tanggal X (kamar
 * sudah kosong hari itu).
 */
export function dateRangesOverlap(
    startA: string | Date,
    endA: string | Date,
    startB: string | Date,
    endB: string | Date
): boolean {
    const a1 = new Date(startA).getTime();
    const a2 = new Date(endA).getTime();
    const b1 = new Date(startB).getTime();
    const b2 = new Date(endB).getTime();
    return a1 < b2 && b1 < a2;
}
